import { apiFetch, readErrorMessage, readJson } from './client';
import type { PatientTrackingImportResult } from '@jarvis/platform-ui';

type PatientTrackingImportKind = 'covered-entities' | 'patients' | 'tracker';

async function importPatientTrackingCsv(kind: PatientTrackingImportKind, file: File): Promise<PatientTrackingImportResult> {
  const form = new FormData();
  form.append('file', file);
  const res = await apiFetch(`/api/patient-tracking/import/${kind}/csv`, {
    method: 'POST',
    body: form
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<PatientTrackingImportResult>(res);
}

function templateUrl(kind: PatientTrackingImportKind): string {
  return `/api/patient-tracking/import/${kind}/template`;
}

async function downloadPatientTrackingTemplate(
  kind: PatientTrackingImportKind,
  fileName: string
): Promise<void> {
  const res = await apiFetch(templateUrl(kind));
  if (!res.ok) throw new Error(await readErrorMessage(res));
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export const patientTrackingImportApi = {
  coveredEntities: {
    templateUrl: templateUrl('covered-entities'),
    templateFileName: 'covered-entities-import-template.csv',
    importCsv: (file: File) => importPatientTrackingCsv('covered-entities', file),
    downloadTemplate: () =>
      downloadPatientTrackingTemplate('covered-entities', 'covered-entities-import-template.csv')
  },
  patients: {
    templateUrl: templateUrl('patients'),
    templateFileName: 'patients-import-template.csv',
    importCsv: (file: File) => importPatientTrackingCsv('patients', file),
    downloadTemplate: () => downloadPatientTrackingTemplate('patients', 'patients-import-template.csv')
  },
  tracker: {
    templateUrl: templateUrl('tracker'),
    templateFileName: 'patient-tracker-import-template.csv',
    importCsv: (file: File) => importPatientTrackingCsv('tracker', file),
    downloadTemplate: () => downloadPatientTrackingTemplate('tracker', 'patient-tracker-import-template.csv')
  }
};
